import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';
import { Spinner } from 'react-bootstrap';
import { Prod01 } from '../../constants/ActionType';
import { useMyOrganizations } from '../../utils/useMyOrganizations';
import ProgramEnrollmentFields, { PROGRAMS, selectedPrograms, toProgramForm } from './ProgramEnrollmentFields';

// Patient basics only (demographics, contact, insurance, program, organization). Mirrors backend enrollment.validation.js.
const FIELDS = [
    { name: 'firstname', label: 'First Name', required: true },
    { name: 'lastname', label: 'Last Name', required: true },
    { name: 'emrId', label: 'EHR-ID', required: true },
    { name: 'DOB', label: 'Date of Birth', type: 'date', required: true },
    { name: 'gender', label: 'Gender', options: ['Male', 'Female', 'Other'], required: true },
    { name: 'patientType', label: 'Program', options: ['RPM', 'CCM', 'Both'], required: true },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'mobileNo', label: 'Mobile No' },
    { name: 'phone1', label: 'Phone' },
    { name: 'address', label: 'Address' },
    { name: 'line2', label: 'Address Line 2' },
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
    { name: 'zipCode', label: 'Zip Code' },
    { name: 'insurancecompany', label: 'Insurance Company' },
    { name: 'insuranceId', label: 'Insurance ID' },
];

const toForm = (p) => {
    const v = {};
    FIELDS.forEach((f) => {
        v[f.name] = p && p[f.name] != null ? String(p[f.name]) : '';
    });
    if (v.DOB) v.DOB = v.DOB.slice(0, 10);
    v.organizationId = p && p.organizationId ? String(p.organizationId) : '';
    return v;
};

const PatientBasicsForm = ({ patient, onSaved, onCancel }) => {
    const alert = useAlert();
    const { scope } = useMyOrganizations();
    const orgs = (scope && scope.organizations) || [];
    const [values, setValues] = useState(toForm(patient));
    const [saving, setSaving] = useState(false);
    const [programValues, setProgramValues] = useState(toProgramForm(patient?.programEnrollments));
    const [changedPrograms, setChangedPrograms] = useState([]);
    const [extraPrograms, setExtraPrograms] = useState((patient?.programEnrollments || []).map((p) => p.program));
    const [programLoading, setProgramLoading] = useState(false);
    const editing = !!(patient && patient._id);
    const originalPrograms = editing ? selectedPrograms(patient.patientType, patient.isCCM) : [];
    const programs = [...new Set([...selectedPrograms(values.patientType, patient?.isCCM), ...extraPrograms])];
    const requiredPrograms = programs.filter((p) => !originalPrograms.includes(p) && !programValues[p]?.consentRecordedAt);

    useEffect(() => {
        if (!editing || patient.programEnrollments) return;
        let active = true;
        setProgramLoading(true);
        axios.get(`${Prod01}/enrollment/patients/${patient._id}`).then(({ data }) => {
            if (!active) return;
            setProgramValues(toProgramForm(data.programEnrollments));
            setExtraPrograms((data.programEnrollments || []).map((p) => p.program));
        }).catch((err) => alert.error(err?.response?.data?.message || err.message))
            .finally(() => { if (active) setProgramLoading(false); });
        return () => { active = false; };
    }, [patient, editing, alert]);

    useEffect(() => {
        if (!values.organizationId && orgs.length === 1) setValues((v) => ({ ...v, organizationId: String(orgs[0]._id) }));
    }, [orgs.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (name) => (e) => setValues({ ...values, [name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!values.organizationId) {
            alert.error('Organization is required');
            return;
        }
        const missing = FIELDS.filter((f) => f.required && !values[f.name].trim()).map((f) => f.label);
        if (missing.length) {
            alert.error(`Required: ${missing.join(', ')}`);
            return;
        }
        const body = {};
        Object.keys(values).forEach((k) => {
            if (editing || values[k] !== '') body[k] = values[k];
        });
        if (!body.DOB) delete body.DOB;
        const submitted = programs.filter((p) => requiredPrograms.includes(p) || changedPrograms.includes(p));
        const today = new Date().toISOString().slice(0, 10);
        for (const program of submitted) {
            const row = programValues[program] || {};
            if (!row.startDate || row.startDate > today || row.consentGiven !== 'yes' || !row.consentDate || row.consentDate > today || !row.consentMethod) {
                alert.error(`${program}: a start date and patient consent (Yes, date and method) are required; dates cannot be in the future`);
                return;
            }
        }
        if (submitted.length) body.programEnrollments = submitted.map((program) => {
            const row = programValues[program];
            return { program, startDate: row.startDate, consentGiven: row.consentGiven === 'yes', consentDate: row.consentDate, consentMethod: row.consentMethod };
        });
        setSaving(true);
        try {
            const { data } = editing
                ? await axios.put(`${Prod01}/enrollment/patients/${patient._id}`, body)
                : await axios.post(`${Prod01}/enrollment/patients`, body);
            alert.success(editing ? 'Patient updated' : 'Patient added');
            if (onSaved) onSaved(data);
        } catch (err) {
            alert.error(err?.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <div className="row">
                <div className="col-md-12">
                    <label className="form-label mt-2">
                        Organization <span style={{ color: 'red' }}>*</span>
                    </label>
                    <select className="form-control" value={values.organizationId} onChange={set('organizationId')}>
                        <option value="">Select organization</option>
                        {orgs.map((o) => (
                            <option key={o._id} value={String(o._id)}>{o.name}</option>
                        ))}
                    </select>
                </div>
                {FIELDS.map((f) => (
                    <div className="col-md-6" key={f.name}>
                        <label className="form-label mt-2">
                            {f.label} {f.required ? <span style={{ color: 'red' }}>*</span> : null}
                        </label>
                        {f.options ? (
                            <select className="form-control" value={values[f.name]} onChange={set(f.name)}>
                                <option value="">Select</option>
                                {f.options.map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                        ) : (
                            <input className="form-control" type={f.type || 'text'} value={values[f.name]} onChange={set(f.name)} />
                        )}
                    </div>
                ))}
                <ProgramEnrollmentFields programs={programs} values={programValues} requiredPrograms={requiredPrograms}
                    onChange={(program, field, value) => {
                        setProgramValues((v) => ({ ...v, [program]: { ...v[program], [field]: value } }));
                        setChangedPrograms((v) => [...new Set([...v, program])]);
                    }} />
                <div className="col-md-12 mt-2">
                    <label>Additional program
                        <select className="form-control" value="" onChange={(e) => setExtraPrograms((v) => [...new Set([...v, e.target.value])])}>
                            <option value="">Add program</option>{PROGRAMS.filter((p) => !programs.includes(p)).map((p) => <option key={p}>{p}</option>)}
                        </select>
                    </label>
                </div>
            </div>
            <hr />
            <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                {onCancel ? (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
                ) : null}
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || programLoading}>
                    {saving ? <Spinner animation="border" size="sm" /> : editing ? 'Save basics' : 'Add patient'}
                </button>
            </div>
        </form>
    );
};

export default PatientBasicsForm;
